import React from 'react';
import { PageContainer } from '@ant-design/pro-layout';
import { Button, Space, Typography } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { ProTable, Link } from '../../layout';
import {
  GET_EMAIL_TEMPLATES,
  TOGGLE_DISABLED_EMAIL_TEMPLATE,
} from '../../../gql/emailTemplates';
import {
  ENUM_EMAIL_CATEGORIES,
  // ENUM_ROLE_RECIPIENT_LEVEL,
  ENUM_RECIPIENT_TYPE,
} from '../../../constants/form';
import { EditButton } from 'components/button';
import { useLoading } from 'hooks';

// const RECIPIENT_COL_WIDTH = 168;
const { Paragraph } = Typography;

const getRecipientsList = (recipients) => {
  const string = recipients
    .map(({ type, role }) => {
      switch (type) {
        case 'PL':
        case 'APPROVER':
          return ENUM_RECIPIENT_TYPE[type];
        case 'ROLE':
          return `${role.name}`;
        default:
          return null;
      }
    })
    .join(', ');

  return (
    <Paragraph
      ellipsis={{ rows: 2, tooltip: string }}
      style={{ marginBottom: 0 }}
    >
      {string}
    </Paragraph>
  );
};

const DisabledButton = ({ disabled, onClick }) =>
  disabled ? (
    <Button onClick={onClick}>Active</Button>
  ) : (
    <Button onClick={onClick}>Disable</Button>
  );

const EmailTemplates = () => {
  const { data, loading } = useQuery(GET_EMAIL_TEMPLATES);
  const [
    toggleDisabledEmailTemplate,
    { loading: disableLoading },
  ] = useMutation(TOGGLE_DISABLED_EMAIL_TEMPLATE, {
    refetchQueries: ({
      data: {
        toggleDisabledEmailTemplate: { id },
      },
    }) => [{ query: GET_EMAIL_TEMPLATES, variables: { ids: [id] } }],
    onError: (err) => {
      console.log(err);
    },
  });
  useLoading(disableLoading);
  const columns = [
    {
      title: 'Email ID',
      dataIndex: 'emailId',
      width: 96,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.emailId - b.emailId,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      filters: true,
      onFilter: true,
      valueEnum: ENUM_EMAIL_CATEGORIES,
      width: 160,
    },
    {
      title: 'Name',
      width: 200,
      dataIndex: 'name',
    },
    {
      title: 'From',
      width: 200,
      render: (_, item) => `${item.senderName} <${item.senderEmail}>`,
    },
    {
      title: 'To',
      dataIndex: 'to',
      width: 200,
      render: (_, item) => getRecipientsList(item.to),
    },
    {
      title: 'CC',
      dataIndex: 'cc',
      render: (_, item) => getRecipientsList(item.cc),
    },
    {
      title: 'Status',
      dataIndex: 'disabled',
      width: 120,
      filters: true,
      onFilter: true,
      valueEnum: {
        false: { text: 'Running', status: 'Success' },
        true: { text: 'Stop', status: 'Error' },
      },
    },
    {
      title: 'Actions',
      width: 152,
      render: (_, item) => (
        <Space>
          <Link to={`/system-setting/email-templates/${item.id}`}>
            <EditButton />
          </Link>
          <DisabledButton
            disabled={item.disabled}
            onClick={() => {
              toggleDisabledEmailTemplate({ variables: { id: item.id } });
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <PageContainer
      loading={loading}
      header={{
        title: 'Email Templates',
        extra: [
          <Button type="primary" key="add">
            <Link to="/system-setting/email-templates/add">Add</Link>
          </Button>,
        ],
      }}
    >
      <ProTable
        loading={loading}
        toolbar={
          {
            // settings: false,
          }
        }
        toolBarRender={false}
        scroll={{ x: 1080 }}
        size="small"
        columns={columns}
        expandable={{
          expandedRowRender: (record) => (
            <p style={{ margin: '8px' }}>{record.description}</p>
          ),
          rowExpandable: (record) => !!record.description,
        }}
        rowKey="id"
        search={false}
        dataSource={data?.emailTemplates}
        pagination={false}
      />
    </PageContainer>
  );
};

export default EmailTemplates;
